import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import BrandLogo from "@/components/BrandLogo";

/**
 * PaymentReturn - the page Stripe redirects users back to after checkout.
 *
 * Used for BOTH /payment/success and /payment/cancel (the `mode` prop
 * differentiates them). On success we poll GET /api/billing/payment-status
 * for up to ~20 s so the plan activation lands before we send the user
 * into the app. Activation itself is idempotent server-side (webhook OR
 * this poll - whichever hits first wins).
 */
export default function PaymentReturn({ mode = "success" }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(mode === "cancel" ? "cancelled" : "polling");
  const [planId, setPlanId] = useState(null);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (mode !== "success" || !sessionId) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10; // ~20 s @ 2 s interval

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/payment-status/${sessionId}`);
        if (cancelled) return;
        setPlanId(data.plan_id || null);
        if (data.payment_status === "paid") {
          if (!trackedRef.current) {
            trackedRef.current = true;
            track("upgrade.purchase_completed", {
              plan_id: data.plan_id,
              session_id: sessionId,
            });
          }
          setState("paid");
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setState("failed");
          return;
        }
        if (attempts >= maxAttempts) {
          setState("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        if (attempts >= maxAttempts) {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
  }, [sessionId, mode]);

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-zinc-100 flex flex-col">
      <header className="px-6 py-5 md:px-10 md:py-6">
        <BrandLogo variant="lockup" to="/" testid="payment-return-brand" />
      </header>
      <main className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          {state === "polling" && <PollingCard />}
          {state === "paid" && <SuccessCard planId={planId} onContinue={() => navigate("/app/dashboard")} />}
          {state === "cancelled" && <CancelledCard />}
          {state === "failed" && <FailedCard />}
          {state === "timeout" && <TimeoutCard sessionId={sessionId} />}
          {state === "error" && <ErrorCard />}
        </div>
      </main>
    </div>
  );
}

function PollingCard() {
  return (
    <div
      data-testid="payment-return-polling"
      className="rounded-2xl border border-amber-500/25 bg-zinc-900/60 backdrop-blur-md p-8 text-center"
    >
      <div className="mx-auto h-14 w-14 rounded-full bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-amber-400 animate-spin" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mt-6">Payment</div>
      <h1 className="text-2xl font-semibold text-zinc-50 mt-1">Confirming your payment…</h1>
      <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
        Stripe is finalizing the transaction. This usually takes just a few seconds, hang tight and don&apos;t close this tab.
      </p>
    </div>
  );
}

function SuccessCard({ planId, onContinue }) {
  const label = planId === "career_pro" ? "Career Pro" : planId === "interview_ready_pass" ? "Interview Ready Pass" : "Your plan";
  return (
    <div
      data-testid="payment-return-success"
      className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.04] p-8 text-center"
    >
      <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
        <CheckCircle2 className="h-7 w-7 text-emerald-400" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.28em] text-emerald-400 mt-6">Payment confirmed</div>
      <h1 className="text-2xl font-semibold text-zinc-50 mt-1">{label} is unlocked</h1>
      <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
        You&apos;re all set. Every feature in your plan is live on your account, head back to the dashboard to keep preparing.
      </p>
      <button
        onClick={onContinue}
        data-testid="payment-return-continue-btn"
        className="mt-6 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold rounded-lg px-5 py-2.5 text-sm shadow-[0_0_20px_-6px_rgba(16,185,129,0.6)] transition-colors"
      >
        Continue to dashboard
      </button>
    </div>
  );
}

function CancelledCard() {
  return (
    <div
      data-testid="payment-return-cancelled"
      className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-8 text-center"
    >
      <div className="mx-auto h-14 w-14 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center">
        <XCircle className="h-7 w-7 text-zinc-400" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.28em] text-zinc-500 mt-6">Payment cancelled</div>
      <h1 className="text-2xl font-semibold text-zinc-50 mt-1">No charge was made</h1>
      <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
        You cancelled before completing checkout. Your card was never charged. Come back whenever you&apos;re ready.
      </p>
      <div className="mt-6 flex items-center justify-center gap-3">
        <Link
          to="/app/billing"
          data-testid="payment-return-back-billing"
          className="inline-flex items-center gap-2 text-sm border border-zinc-800 hover:bg-zinc-900 text-zinc-100 rounded-lg px-4 py-2 transition-colors"
        >
          Back to Billing
        </Link>
        <Link
          to="/app/dashboard"
          data-testid="payment-return-back-dashboard"
          className="inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 transition-colors"
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

function FailedCard() {
  return (
    <div
      data-testid="payment-return-failed"
      className="rounded-2xl border border-rose-500/25 bg-rose-500/[0.05] p-8 text-center"
    >
      <div className="mx-auto h-14 w-14 rounded-full bg-rose-500/10 border border-rose-500/25 flex items-center justify-center">
        <AlertCircle className="h-7 w-7 text-rose-300" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.28em] text-rose-300 mt-6">Payment failed</div>
      <h1 className="text-2xl font-semibold text-zinc-50 mt-1">We couldn&apos;t complete your payment</h1>
      <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
        Your card was declined or the session expired. Nothing was charged. Please try again from the Billing page, Stripe will let you pick a different card.
      </p>
      <Link
        to="/app/billing"
        data-testid="payment-return-retry-billing"
        className="mt-6 inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 transition-colors"
      >
        Back to Billing
      </Link>
    </div>
  );
}

function TimeoutCard({ sessionId }) {
  return (
    <div
      data-testid="payment-return-timeout"
      className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-8 text-center"
    >
      <div className="mx-auto h-14 w-14 rounded-full bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
        <AlertCircle className="h-7 w-7 text-amber-300" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mt-6">Still confirming…</div>
      <h1 className="text-2xl font-semibold text-zinc-50 mt-1">This is taking longer than usual</h1>
      <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
        Stripe hasn&apos;t confirmed the payment yet. Your card may still be processing, your plan will activate automatically as soon as it lands. Refresh the Billing page in a minute to check.
      </p>
      {sessionId && (
        <p className="mt-3 text-[10px] text-zinc-600 font-mono break-all">Ref: {sessionId}</p>
      )}
      <Link
        to="/app/billing"
        data-testid="payment-return-timeout-billing"
        className="mt-6 inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 transition-colors"
      >
        Back to Billing
      </Link>
    </div>
  );
}

function ErrorCard() {
  return (
    <div
      data-testid="payment-return-error"
      className="rounded-2xl border border-rose-500/25 bg-rose-500/[0.05] p-8 text-center"
    >
      <div className="mx-auto h-14 w-14 rounded-full bg-rose-500/10 border border-rose-500/25 flex items-center justify-center">
        <AlertCircle className="h-7 w-7 text-rose-300" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.28em] text-rose-300 mt-6">Couldn&apos;t reach our server</div>
      <h1 className="text-2xl font-semibold text-zinc-50 mt-1">We couldn&apos;t confirm your payment</h1>
      <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
        We couldn&apos;t reach Intuiterview to confirm the payment. If your card was charged, your plan will still activate, check the Billing page in a few minutes.
      </p>
      <Link
        to="/app/billing"
        data-testid="payment-return-error-billing"
        className="mt-6 inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 transition-colors"
      >
        Back to Billing
      </Link>
    </div>
  );
}
